import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Check, Loader2, Sparkles, XCircle } from "lucide-react";

const MAX_POLLS = 20;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [state, setState] = useState("polling"); // polling | success | timeout | error
  const polls = useRef(0);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) { setState("error"); return; }
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      if (polls.current >= MAX_POLLS) { setState("timeout"); return; }
      polls.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refresh();
          if (!cancelled) setState("success");
          return;
        }
        if (data.status === "failed" || data.payment_status === "failed") { setState("error"); return; }
      } catch { /* keep polling */ }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [params, refresh]);

  return (
    <div className="max-w-md mx-auto px-6 py-24 text-center">
      {state === "polling" && (
        <div data-testid="payment-polling" className="animate-fade-up">
          <Loader2 className="w-10 h-10 animate-spin mx-auto mb-6 text-nerd-light/60" />
          <h1 className="font-display text-2xl font-extrabold mb-2">Confirming your payment…</h1>
          <p className="text-nerd-light/55">Hang tight, this only takes a moment.</p>
        </div>
      )}
      {state === "success" && (
        <div data-testid="payment-success" className="animate-fade-up">
          <div className="w-16 h-16 rounded-2xl bg-nerd-light flex items-center justify-center mx-auto mb-6">
            <Check className="w-8 h-8 text-white" />
          </div>
          <h1 className="font-display text-3xl font-extrabold mb-2">Welcome to Premium!</h1>
          <p className="text-nerd-light/55 mb-8">Your AI Creative Agent, planner and supplies are now unlocked.</p>
          <button data-testid="go-to-agent-btn" onClick={() => navigate("/create")}
            className="inline-flex items-center gap-2 bg-nerd-light text-white px-6 py-3 rounded-full text-sm font-bold hover:opacity-90 transition-opacity">
            <Sparkles className="w-4 h-4" /> Start cooking with AI
          </button>
        </div>
      )}
      {(state === "timeout" || state === "error") && (
        <div data-testid="payment-error" className="animate-fade-up">
          <XCircle className="w-12 h-12 mx-auto mb-5 text-nerd-light/40" />
          <h1 className="font-display text-2xl font-extrabold mb-2">
            {state === "timeout" ? "Still processing…" : "Something went wrong"}
          </h1>
          <p className="text-nerd-light/55 mb-8">
            {state === "timeout"
              ? "Your payment is taking longer than expected. It may still complete shortly — check your profile in a minute."
              : "We couldn't confirm your payment. If you were charged, it will reflect shortly."}
          </p>
          <Link to="/" className="inline-flex items-center gap-2 border border-black/12 px-6 py-3 rounded-full text-sm font-semibold hover:bg-black/5 transition-colors">
            Back to Discover
          </Link>
        </div>
      )}
    </div>
  );
}
